/**
 * The audit checkpoint sheet — DECISIONS.md #53 step 2. Mirrors `Audit/CheckpointEndpoints.cs`.
 *
 * **Fetching a sheet writes a `checkpoint` row** (migration 0018), which is what lets the reminder
 * know when the last one was printed and lets an investigation ask which sheets exist. So it is a
 * `POST`, made once when the sheet is opened — the same terms as a patient print, which is audited
 * the moment its view opens.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/** Mirrors `CheckpointStatus`. */
export interface CheckpointStatus {
  /** ISO 8601, or null when none has been printed. */
  readonly lastPrintedAt: string | null
  readonly overdue: boolean
}

/** Mirrors `CheckpointSheet`. */
export interface CheckpointSheet {
  readonly seq: number
  readonly hash: string
  readonly fingerprint: string
  readonly anchoredAt: string
  readonly printedAt: string

  /** `--verify-audit --after-seq … --anchor-hash …`, both values filled in by the server. */
  readonly verifyArguments: string
}

/** Any failure. The reminder stays quiet on one; the sheet says it and offers a retry. */
export class CheckpointUnavailableError extends Error {
  constructor() {
    super('Checkpoint unavailable')
    this.name = 'CheckpointUnavailableError'
  }
}

async function call<T>(method: 'GET' | 'POST', signal: AbortSignal): Promise<T> {
  let response: Response

  try {
    response = await fetch('/api/audit/checkpoint', {
      method,
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new CheckpointUnavailableError()
  }

  unauthenticated(response)

  if (!response.ok) {
    throw new CheckpointUnavailableError()
  }

  try {
    return (await response.json()) as T
  } catch {
    throw new CheckpointUnavailableError()
  }
}

/** When the last sheet was printed. Not audited — it says nothing about anybody's record. */
export function fetchCheckpointStatus(signal: AbortSignal): Promise<CheckpointStatus> {
  return call<CheckpointStatus>('GET', signal)
}

/** Produces a sheet, and records that it was printed. */
export function printCheckpoint(signal: AbortSignal): Promise<CheckpointSheet> {
  return call<CheckpointSheet>('POST', signal)
}
